import React from "react";
import {
  Button,
  Typography,
  Table,
  Space,
  Spin,
  Modal,
  Alert,
  Empty,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getWizards, deleteWizard } from "../../../services/wizardCmsService";
import { getPublicWizardUrl } from "../utils/url";
import { WizardSummary } from "../types";

const { Title } = Typography;

const WizardListPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const deleteWizardMutation = useMutation<void, Error, string>({
    mutationFn: deleteWizard,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wizards"] });
    },
  });

  const handleEdit = (wizardId: string) => {
    navigate(`/wizards/${wizardId}/edit`);
  };

  const handleDelete = (wizardId: string) => {
    Modal.confirm({
      title: "Are you sure you want to delete this wizard?",
      icon: <ExclamationCircleOutlined />,
      content: "This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "No, Cancel",
      onOk() {
        deleteWizardMutation.mutate(wizardId);
      },
    });
  };

  const {
    data: wizards,
    isLoading,
    isError,
  } = useQuery<WizardSummary[], Error>({
    queryKey: ["wizards"],
    queryFn: getWizards,
  });

  const columns = [
    {
      title: "Wizard Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Last Modified",
      dataIndex: "modifiedAt",
      key: "modifiedAt",
      render: (text: string) => new Date(text).toLocaleString(),
    },
    {
      title: "Path",
      dataIndex: "wizardId",
      key: "wizardId",
      render: (wizardId: string) => {
        const url = getPublicWizardUrl(wizardId);
        return (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {url}
          </a>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: { wizardId: string }) => (
        <Space size="middle">
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record.wizardId)}
          >
            Edit
          </Button>
          <Button
            icon={<DeleteOutlined />}
            danger
            onClick={() => handleDelete(record.wizardId)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  if (isError) {
    return (
      <Alert
        message="Error"
        description="Could not load wizards. Please try again later."
        type="error"
        showIcon
      />
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <Title level={2} style={{ marginBottom: 0 }}>
          Wizard Management
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate("/wizards/new")}
        >
          Create New Wizard
        </Button>
      </div>

      {isLoading && (
        <Spin size="large" style={{ display: "block", marginTop: "50px" }} />
      )}
      {!isLoading && !wizards?.length && (
        <Empty description="No wizards found.">
          <Button type="primary" onClick={() => navigate("/wizards/new")}>
            Create One Now
          </Button>
        </Empty>
      )}
      {!isLoading && wizards && wizards.length > 0 && (
        <Table columns={columns} dataSource={wizards} rowKey="wizardId" />
      )}
    </div>
  );
};

export default WizardListPage;
